import React, { useRef } from "react";
import Swal from 'sweetalert2';
import { Link } from "react-router-dom";
import { isMobile } from "react-device-detect";
import AuthSlider from "./auth-slider";

function NewRegister() {
  const [errors, setErrors] = React.useState({});
  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const emailRef = useRef();
  const contactRef = useRef();
  const instituteRef = useRef();
  const agreeRef = useRef();

  const API = process.env.REACT_APP_API_URL;

  const validateForm = () => {
    const newErrors = {};
    
    if (!firstNameRef.current.value) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!lastNameRef.current.value) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!emailRef.current.value) {
      newErrors.email = 'Email is required';
    } else {
      const email = emailRef.current.value;
      if (!email.includes('@')) {
        newErrors.email = 'Email must contain @ symbol';
      } else if (!email.toLowerCase().endsWith('.com')) {
        newErrors.email = 'Email must end with .com domain';
      }
    }
    
    if (!contactRef.current.value) {
      newErrors.contact = 'Contact number is required';
    } else if (!/^\d{10}$/.test(contactRef.current.value)) {
      newErrors.contact = 'Please enter a valid 10-digit contact number';
    }
    
    if (!instituteRef.current.value) {
      newErrors.institute = 'Institute name is required';
    }

    if (!agreeRef.current.checked) {
      newErrors.agree = 'You must agree to the Terms and Conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
  
    const payload = {
      first_name: firstNameRef.current.value,
      last_name: lastNameRef.current.value,
      email: emailRef.current.value,
      contact_number: contactRef.current.value,
      institution_name: instituteRef.current.value,
      accept: agreeRef.current.checked,
      provider_id: null,
      is_premium: false
    };
  
    try {
      const response = await fetch(`${API}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
  
      const data = await response.json();
  
      if (data.success === true) {
        
        // Show success SweetAlert
        Swal.fire({
          icon: 'success',
          title: 'Registration Successful!',
          text: 'You have been registered successfully. Our team will contact you, and after course purchase is done, you will get login credentials on your registered Email.',
          confirmButtonColor: '#28a745',
          confirmButtonText: 'OK'
        }).then(() => {
          // Clear the form
          firstNameRef.current.value = '';
          lastNameRef.current.value = '';
          emailRef.current.value = '';
          contactRef.current.value = '';
          instituteRef.current.value = '';
          agreeRef.current.checked = false;
          setErrors({}); // Clear any previous errors
        });
        
      } else {
        
        if (data.message && data.message.toLowerCase().includes("email has already been taken")) {
          setErrors({
            email: "This email is already registered. Please use a different email address."
          });
          
          // Also show SweetAlert for better user experience
          Swal.fire({
            icon: 'error',
            title: 'Email Already Registered',
            text: 'This email address is already registered. Please use a different email or try signing in instead.',
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'OK'
          });
        } else {
          // Handle other API errors
          setErrors(prev => ({...prev, submit: data.message || "An unknown error occurred"}));
        }
      }
    } catch (networkError) {
      console.error("❌ Network error during registration:", networkError);
      setErrors(prev => ({...prev, submit: "Registration failed. Please check your internet connection and try again."}));
    }
  };

  const handleInputChange = (field) => {
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className="min-vh-100 overflow-hidden">
      <div className="container-fluid h-100 p-0">
        <div className="row g-0 h-100 flex-nowrap">
          <AuthSlider/>
          <div className="col-lg-6 offset-lg-6 col-12 d-flex align-items-center justify-content-center bg-white" style={{ minHeight: isMobile ? "90vh":"100vh", overflowY: "auto" }}>
            <div className="w-100 p-3 p-md-1 mt-4" style={{ maxWidth: "450px" }}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center">
                  <div className="d-flex align-items-center justify-content-center me-3">
                    <img src="/assets/images/certificate/certificate_logo.png" width={180} height={40}/>
                  </div>
                </div>
              </div>

              <div className="login-form">
                <h3 className="mb-4 fw-bold text-balance" style={{ color: "#2D3748", fontSize: "2rem", lineHeight: "1.3" }}>
                  Create Your Account
                </h3>

                <form onSubmit={handleSubmit}>
                  {errors.submit && (
                    <div className="alert alert-danger" role="alert">
                      {errors.submit}
                    </div>
                  )}
                  <div className="row">
                    <div className="col-md-6 mb-3 p-1">
                    <label
                      htmlFor="firstName"
                      className="form-label fw-semibold mb-2"
                      style={{ color: "#4A5568", fontSize: "0.95rem" }}
                    >
                      First Name <span style={{ color: "#260efcff" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control form-control-lg ${errors.firstName ? 'is-invalid' : ''}`}
                      id="firstName"
                      name="firstName"
                      ref={firstNameRef}
                      onChange={() => handleInputChange("firstName")}
                      placeholder="Enter first name"
                      style={{
                        border: "2px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "14px 16px",
                        fontSize: "1rem",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#260efcff")}
                      onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                    />
                    {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                    </div>
                    <div className="col-md-6 mb-3 p-1">
                    <label
                      htmlFor="lastName"
                      className="form-label fw-semibold mb-2"
                      style={{ color: "#4A5568", fontSize: "0.95rem" }}
                    >
                      Last Name <span style={{ color: "#260efcff" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control form-control-lg ${errors.lastName ? 'is-invalid' : ''}`}
                      id="lastName"
                      name="lastName"
                      ref={lastNameRef}
                      onChange={() => handleInputChange("lastName")}
                      placeholder="Enter last name"
                      style={{
                        border: "2px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "14px 16px",
                        fontSize: "1rem",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#260efcff")}
                      onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                    />
                    {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3 p-1">
                      <label
                        htmlFor="email"
                        className="form-label fw-semibold mb-2"
                        style={{ color: "#4A5568", fontSize: "0.95rem" }}
                        >
                        Email <span style={{ color: "#260efcff" }}>*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control form-control-lg ${errors.email ? 'is-invalid' : ''}`}
                        id="email"
                        name="email"
                        ref={emailRef}
                        onChange={() => handleInputChange("email")}
                        placeholder="Enter Email ID"
                        style={{
                            border: "2px solid #E2E8F0",
                            borderRadius: "12px",
                            padding: "14px 16px",
                            fontSize: "1rem",
                        }}
                        onFocus={(e) => (e.target.style.borderColor = "#260efcff")}
                        onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                    <div className="col-md-6 mb-3 p-1">
                      <label
                        htmlFor="contact"
                        className="form-label fw-semibold mb-2"
                        style={{ color: "#4A5568", fontSize: "0.95rem" }}
                        >
                        Contact Number <span style={{ color: "#260efcff" }}>*</span>
                      </label>
                      <input
                        type="tel"
                        className={`form-control form-control-lg ${errors.contact ? 'is-invalid' : ''}`}
                        id="contact"
                        name="contact"
                        ref={contactRef}
                        onChange={() => handleInputChange("contact")}
                        maxLength={10}
                        pattern="[0-9]{10}"
                        placeholder="Enter contact number"
                        onKeyUp={(e) => {
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        style={{
                            border: "2px solid #E2E8F0",
                            borderRadius: "12px",
                            padding: "14px 16px",
                            fontSize: "1rem",
                        }}
                        onFocus={(e) => (e.target.style.borderColor = "#260efcff")}
                        onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                      />
                      {errors.contact && <div className="invalid-feedback">{errors.contact}</div>}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="institute"
                      className="form-label fw-semibold mb-2"
                      style={{ color: "#4A5568", fontSize: "0.95rem" }}
                    >
                      Institute Name <span style={{ color: "#260efcff" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control form-control-lg ${errors.institute ? 'is-invalid' : ''}`}
                      id="institute"
                      name="institute"
                      placeholder="Enter institute name"
                      ref={instituteRef}
                      onChange={() => handleInputChange("institute")}
                      style={{
                        border: "2px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "14px 16px",
                        fontSize: "1rem",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#260efcff")}
                      onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                    />
                    {errors.institute && <div className="invalid-feedback">{errors.institute}</div>}
                  </div>

                  <div className={`form-check mb-3 ${errors.agree ? 'is-invalid' : ''}`}>
                    <input
                      className={`form-check-input ${errors.agree ? 'is-invalid' : ''}`}
                      type="checkbox"
                      id="agree" 
                      name="agree" 
                      ref={agreeRef}
                      onChange={() => handleInputChange("agree")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="agree"
                      style={{ fontSize: "0.9rem", color: "#4A5568" }}
                      >
                      I agree to the{" "}
                      <a href="https://hunarho.com/terms-and-conditions/" className="fw-semibold" style={{ color: "#260efcff" }}>
                        Terms and Conditions
                      </a>
                    </label>
                    {errors.agree && <div className="invalid-feedback">{errors.agree}</div>}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-lg w-100 text-white fw-semibold"
                    style={{
                      background: "linear-gradient(135deg, #260efcff 0%, #2b05ffff 80%)",
                      border: "none",
                      borderRadius: "12px",
                      padding: "14px",
                      fontSize: "1.1rem",
                    }}
                  >
                    Register
                  </button>
                
                </form>
                <div className="text-center mt-2">
                  <span className="fw-medium" style={{ color: "#0d0d0eff", fontSize: "0.95rem" }}>
                    Already have an account?{" "}
                  </span>
                  <Link
                    to="/login"
                    className="text-decoration-none fw-semibold"
                    style={{ color: "#260efcff", fontSize: "0.95rem" }}
                  >
                    Sign In
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewRegister
